import React, { useEffect, useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { apiService } from '../../../../components/api/apiService';
import { getConnectorDisplayName } from '../../Connections/catalog';
import { listDataStores, type DataStoreSummary } from '../../Connections/dataStoreApi';
import { logDataModels } from '../dataModelsLogger';
import type { DataSourceKind } from '../dataModelsTypes';
import { DialogShell, SourceIconTile, ghostBtn, primaryBtn } from './dataModelsUi';

export type DiscoveredModelAsset = {
  id: string;
  name: string;
  sourceId: string;
  sourceName: string;
  sourceKind: DataSourceKind;
  environment: string;
  connectionType: string;
  connectorKey?: string;
  path?: string;
  mimeType?: string;
  assetKind: 'Table' | 'Collection' | 'File' | 'View';
};

type SchemaNode = {
  name?: string;
  Name?: string;
  type?: string;
  Type?: string;
  children?: SchemaNode[];
  Children?: SchemaNode[];
  path?: string;
  Path?: string;
  mimeType?: string;
  MimeType?: string;
  mime?: string;
  expandable?: boolean;
  Expandable?: boolean;
};

const ASSET_TYPES = new Set(['table', 'view', 'collection', 'file', 'object']);
const MAX_EXPANDS = 24;

export default function ConnectionAssetBrowser({
  isLight,
  selected,
  sourceIds,
  onClose,
  onApply,
}: {
  isLight: boolean;
  selected: DiscoveredModelAsset[];
  sourceIds: string[];
  onClose: () => void;
  onApply: (assets: DiscoveredModelAsset[]) => void;
}) {
  const [connections, setConnections] = useState<DataStoreSummary[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionsError, setConnectionsError] = useState('');
  const [connectionQuery, setConnectionQuery] = useState('');
  const [activeId, setActiveId] = useState('');
  const [catalogs, setCatalogs] = useState<Record<string, ConnectionCatalog>>({});
  const [draft, setDraft] = useState<DiscoveredModelAsset[]>(selected);
  const [assetQuery, setAssetQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setConnectionsLoading(true);
    setConnectionsError('');
    listDataStores()
      .then((rows) => {
        if (cancelled) return;
        setConnections(rows);
        logDataModels('app', 'model_browse_connections_loaded', { count: rows.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load connections';
        setConnectionsError(message);
        logDataModels('error', 'model_browse_connections_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setConnectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const workspaceConnections = useMemo(() => {
    const allowed = new Set(sourceIds);
    return connections.filter((connection) => allowed.has(connection.id));
  }, [connections, sourceIds]);

  const visibleConnections = useMemo(() => {
    const needle = connectionQuery.trim().toLowerCase();
    if (!needle) return workspaceConnections;
    return workspaceConnections.filter((connection) =>
      `${connection.name} ${getConnectorDisplayName(connection.connectorKey)} ${connection.environment || ''}`.toLowerCase().includes(needle)
    );
  }, [connectionQuery, workspaceConnections]);

  const active = connections.find((connection) => connection.id === activeId) ?? null;
  const activeCatalog = activeId ? catalogs[activeId] : undefined;
  const visibleAssets = useMemo(() => {
    const assets = activeCatalog?.status === 'ready' ? activeCatalog.assets : [];
    const needle = assetQuery.trim().toLowerCase();
    if (!needle) return assets;
    return assets.filter((asset) => `${asset.name} ${asset.assetKind}`.toLowerCase().includes(needle));
  }, [activeCatalog, assetQuery]);

  const openConnection = (connection: DataStoreSummary) => {
    setActiveId(connection.id);
    setAssetQuery('');
    if (catalogs[connection.id]) return;
    setCatalogs((current) => ({ ...current, [connection.id]: { status: 'loading', assets: [] } }));
    loadConnectionAssets(connection)
      .then((assets) => {
        setCatalogs((current) => ({ ...current, [connection.id]: { status: 'ready', assets } }));
        logDataModels('app', 'model_browse_assets_loaded', { sourceId: connection.id, count: assets.length });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Unable to load assets';
        setCatalogs((current) => ({ ...current, [connection.id]: { status: 'error', assets: [], error: message } }));
        logDataModels('error', 'model_browse_assets_failed', { sourceId: connection.id, message });
      });
  };

  const toggle = (asset: DiscoveredModelAsset) => {
    setDraft((current) => (current.some((item) => item.id === asset.id) ? current.filter((item) => item.id !== asset.id) : [...current, asset]));
  };

  const visibleIds = visibleAssets.map((asset) => asset.id);
  const selectedVisible = visibleIds.filter((id) => draft.some((item) => item.id === id)).length;
  const allOn = selectedVisible === visibleIds.length && visibleIds.length > 0;
  const mixed = selectedVisible > 0 && !allOn;
  const rail = isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]';
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';

  return (
    <DialogShell
      title="Select assets"
      subtitle="Choose a connection, then pick its tables, collections, or files."
      isLight={isLight}
      onClose={onClose}
      size="large"
      footer={
        <>
          <span className={`mr-auto self-center text-[12px] ${muted}`}>{draft.length} selected</span>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={primaryBtn()}
            onClick={() => {
              logDataModels('app', 'model_browse_applied', { count: draft.length });
              onApply(draft);
            }}
          >
            Apply
          </button>
        </>
      }
    >
      <div className="flex h-[min(480px,calc(100vh-16rem))] min-h-[22rem]">
        <nav className={`flex w-[240px] shrink-0 flex-col border-r ${rail}`}>
          <div className="p-2 pb-1">
            <p className={`px-2 pb-2 pt-1 text-[10px] font-semibold uppercase tracking-wide ${muted}`}>Connections</p>
            <label className="relative block">
              <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                value={connectionQuery}
                onChange={(event) => setConnectionQuery(event.target.value)}
                placeholder="Filter connections"
                className={isLight
                  ? 'h-8 w-full rounded-md border border-[#D8DCE2] bg-white py-0 pl-8 pr-3 text-sm text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8] focus:outline-none'
                  : 'h-8 w-full rounded-md border border-white/15 bg-black py-0 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none'}
              />
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2 pt-1">
            {connectionsLoading ? <p className={`px-2 py-3 text-[12px] ${muted}`}>Loading connections…</p> : null}
            {connectionsError ? <p className="px-2 py-3 text-[12px] text-rose-600">{connectionsError}</p> : null}
            {visibleConnections.map((connection) => {
              const activeRow = connection.id === activeId;
              const picked = draft.filter((asset) => asset.sourceId === connection.id).length;
              const type = getConnectorDisplayName(connection.connectorKey);
              return (
                <button
                  key={connection.id}
                  type="button"
                  onClick={() => openConnection(connection)}
                  className={`mb-0.5 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left ${
                    activeRow ? (isLight ? 'bg-white text-[#111827] shadow-sm' : 'bg-white/10 text-white') : muted
                  }`}
                >
                  <SourceIconTile kind={kindForConnector(connection.connectorKey)} isLight={isLight} box="h-6 w-6" />
                  <span className="min-w-0">
                    <span className={`block truncate text-[12px] ${activeRow ? 'font-medium' : ''}`}>{connection.name}</span>
                    <span className={`block truncate text-[11px] ${muted}`}>
                      {type}
                      {picked ? ` · ${picked} selected` : ''}
                    </span>
                  </span>
                </button>
              );
            })}
            {!connectionsLoading && !connectionsError && !visibleConnections.length ? (
              <p className={`px-2 py-3 text-[12px] ${muted}`}>
                {workspaceConnections.length ? 'No connections match.' : 'No connections in this WorkSpace.'}
              </p>
            ) : null}
          </div>
        </nav>
        <div className="min-w-0 flex-1 overflow-y-auto px-4 py-3">
          {!active ? <p className={`py-8 text-center text-[12px] ${muted}`}>Select a connection to see its tables, collections, and files.</p> : null}
          {active && activeCatalog?.status === 'loading' ? <p className={`py-8 text-center text-[12px] ${muted}`}>Loading assets…</p> : null}
          {active && activeCatalog?.status === 'error' ? <p className="py-8 text-center text-[12px] text-rose-600">{activeCatalog.error}</p> : null}
          {active && activeCatalog?.status === 'ready' ? (
            <>
              <button
                type="button"
                disabled={!visibleAssets.length}
                onClick={() => {
                  setDraft((current) =>
                    allOn
                      ? current.filter((item) => !visibleIds.includes(item.id))
                      : [...current.filter((item) => !visibleIds.includes(item.id)), ...visibleAssets]
                  );
                }}
                className={`mb-3 flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left disabled:opacity-50 ${isLight ? 'border-[#E6E8EB] hover:bg-[#F7F8FA]' : 'border-white/10 hover:bg-white/5'}`}
              >
                <PickBox checked={allOn} mixed={mixed} isLight={isLight} />
                <span className={`text-[12px] font-medium ${ink}`}>{getConnectorDisplayName(active.connectorKey)}</span>
                <span className={`ml-auto text-[11px] ${muted}`}>{selectedVisible}/{visibleAssets.length}</span>
              </button>
              <label className="relative mb-3 block">
                <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
                <input
                  value={assetQuery}
                  onChange={(event) => setAssetQuery(event.target.value)}
                  placeholder="Search tables, collections, files"
                  className={isLight
                    ? 'h-8 w-full rounded-md border border-[#D8DCE2] bg-white py-0 pl-8 pr-3 text-sm text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8] focus:outline-none'
                    : 'h-8 w-full rounded-md border border-white/15 bg-black py-0 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none'}
                />
              </label>
              <div className="space-y-0.5">
                {visibleAssets.map((asset) => {
                  const checked = draft.some((item) => item.id === asset.id);
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => toggle(asset)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}
                    >
                      <PickBox checked={checked} isLight={isLight} />
                      <span className={`min-w-0 flex-1 truncate font-mono text-xs ${ink}`}>{asset.name}</span>
                      <span className={`shrink-0 text-[11px] ${muted}`}>{asset.assetKind}</span>
                    </button>
                  );
                })}
                {!visibleAssets.length ? <p className={`px-2 py-3 text-[12px] ${muted}`}>{activeCatalog.assets.length ? 'No assets match this search.' : 'This connection did not return tables, collections, or files.'}</p> : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </DialogShell>
  );
}

type ConnectionCatalog = {
  status: 'loading' | 'ready' | 'error';
  assets: DiscoveredModelAsset[];
  error?: string;
};

function PickBox({ checked, mixed, isLight }: { checked: boolean; mixed?: boolean; isLight: boolean }) {
  return (
    <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
      checked || mixed ? 'border-[#1B6CA8] bg-[#1B6CA8] text-white' : isLight ? 'border-[#D8DCE2]' : 'border-white/20'
    }`}
    >
      {checked ? <Check size={11} /> : mixed ? <span className="h-0.5 w-2 rounded-sm bg-white" /> : null}
    </span>
  );
}

async function loadConnectionAssets(connection: DataStoreSummary): Promise<DiscoveredModelAsset[]> {
  const discovered = await discoverNodes(connection.id);
  let assets = flattenAssets(discovered, connection);
  if (assets.length) return assets;
  const expanded = await expandContainers(connection.id, discovered);
  assets = flattenAssets(expanded, connection);
  return assets;
}

async function discoverNodes(storeId: string): Promise<SchemaNode[]> {
  const data = await apiService.get(`/api/DataStores/${encodeURIComponent(storeId)}/discover`, {}, 'backend');
  return nodesFrom(data);
}

async function expandContainers(storeId: string, roots: SchemaNode[]): Promise<SchemaNode[]> {
  const tree = roots.map(cloneNode);
  let calls = 0;
  const walk = async (nodes: SchemaNode[], depth: number): Promise<void> => {
    if (depth > 2 || calls >= MAX_EXPANDS) return;
    for (const node of nodes) {
      if (calls >= MAX_EXPANDS) return;
      const children = childNodes(node);
      if (children.length) {
        await walk(children, depth + 1);
        continue;
      }
      const path = nodePath(node);
      if (!path || !isExpandable(node) || ASSET_TYPES.has(nodeType(node))) continue;
      calls += 1;
      try {
        const data = await apiService.get(
          `/api/DataStores/${encodeURIComponent(storeId)}/expand?path=${encodeURIComponent(path)}`,
          {},
          'backend'
        );
        node.children = nodesFrom(data);
      } catch (err) {
        logDataModels('warning', 'model_browse_expand_failed', {
          sourceId: storeId,
          path,
          message: err instanceof Error ? err.message : String(err),
        });
        node.children = [];
      }
      await walk(childNodes(node), depth + 1);
    }
  };
  await walk(tree, 0);
  return tree;
}

function flattenAssets(nodes: SchemaNode[], connection: DataStoreSummary, prefix = ''): DiscoveredModelAsset[] {
  const assets: DiscoveredModelAsset[] = [];
  const seen = new Set<string>();
  const walk = (list: SchemaNode[], parentPath: string) => {
    list.forEach((node) => {
      const name = nodeName(node);
      const path = nodePath(node) || (parentPath ? `${parentPath}.${name}` : name);
      const type = nodeType(node);
      if (ASSET_TYPES.has(type) && name && !seen.has(path)) {
        seen.add(path);
        assets.push({
          id: `${connection.id}:${path}`,
          name,
          sourceId: connection.id,
          sourceName: connection.name,
          sourceKind: kindForConnector(connection.connectorKey),
          environment: connection.environment || '',
          connectionType: getConnectorDisplayName(connection.connectorKey),
          connectorKey: connection.connectorKey,
          path,
          mimeType: nodeMime(node),
          assetKind: assetKind(type),
        });
      }
      const children = childNodes(node);
      if (children.length) walk(children, path);
    });
  };
  walk(nodes, prefix);
  return assets;
}

function nodesFrom(data: unknown): SchemaNode[] {
  if (Array.isArray(data)) return data as SchemaNode[];
  if (!data || typeof data !== 'object') return [];
  const record = data as { nodes?: unknown; Nodes?: unknown };
  if (Array.isArray(record.nodes)) return record.nodes as SchemaNode[];
  if (Array.isArray(record.Nodes)) return record.Nodes as SchemaNode[];
  return [];
}

function cloneNode(node: SchemaNode): SchemaNode {
  return { ...node, children: childNodes(node).map(cloneNode) };
}

function childNodes(node: SchemaNode): SchemaNode[] {
  if (Array.isArray(node.children)) return node.children;
  if (Array.isArray(node.Children)) return node.Children;
  return [];
}

function nodeName(node: SchemaNode): string {
  return String(node.name || node.Name || '').trim();
}

function nodeType(node: SchemaNode): string {
  return String(node.type || node.Type || '').trim().toLowerCase();
}

function nodePath(node: SchemaNode): string {
  return String(node.path || node.Path || '').trim();
}

function nodeMime(node: SchemaNode): string {
  return String(node.mimeType || node.MimeType || node.mime || '').trim();
}

function isExpandable(node: SchemaNode): boolean {
  if (node.expandable || node.Expandable) return true;
  const type = nodeType(node);
  return type === 'folder' || type === 'bucket' || type === 'database';
}

function assetKind(type: string): DiscoveredModelAsset['assetKind'] {
  if (type === 'collection') return 'Collection';
  if (type === 'file' || type === 'object') return 'File';
  if (type === 'view') return 'View';
  return 'Table';
}

function kindForConnector(connectorKey: string): DataSourceKind {
  const key = connectorKey.trim().toLowerCase();
  if (key === 'snowflake') return 'snowflake';
  if (key === 'mongodb' || key === 'firestore') return 'mongodb';
  if (key === 'bigquery') return 'bigquery';
  if (key === 'mssql' || key === 'sqlserver') return 'sqlserver';
  if (key === 'databricks') return 'databricks';
  return 'postgresql';
}
